import { localStorage as safeLocalStorage, sessionStorage as safeSessionStorage } from 'utils/browserStorage';
import React, { useState, useCallback, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import HistoryIcon from '@mui/icons-material/History';
import DescriptionIcon from '@mui/icons-material/Description';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import BarChartIcon from '@mui/icons-material/BarChart';
import SchoolIcon from '@mui/icons-material/School';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { NotificationBell } from '../Notifications';
import { isNativeApp, applyNativeSafeAreaCssVar } from '../../utils/nativeApp';
import {
  isNarrowTalentLayoutViewport,
  subscribeToTalentLayoutChanges,
  shouldHideTalentHeaderAddShift,
} from '../../utils/talentNavLayout';
import styles from './TalentNavbar.module.scss';
import logoBlue from '../../images/logoBlue.svg';
import { useCeDirectoryAvailability } from '../../features/ceDirectory';

function useTalentCompactLayout(): boolean {
  const isNative = isNativeApp();
  const [isNarrowViewport, setIsNarrowViewport] = useState(() => (
    isNative || isNarrowTalentLayoutViewport()
  ));

  useEffect(() => {
    if (isNative) {
      setIsNarrowViewport(true);
      return undefined;
    }

    const sync = () => setIsNarrowViewport(isNarrowTalentLayoutViewport());
    sync();
    return subscribeToTalentLayoutChanges(sync);
  }, [isNative]);

  return isNative || isNarrowViewport;
}

export interface TalentNavbarProps {
  /** User's first name for display in side menu */
  firstName?: string;
  /** User's last name for display in side menu */
  lastName?: string;
  /** User's photo URL for side menu avatar */
  photoUrl?: string;
  /** Whether the user is a hygienist or assistant (vs associate dentist) */
  isHygOrAsst?: boolean;
  /** Whether Instabook is activated for this user */
  instabookActivated?: boolean;
  /** Whether this is a test account */
  isTestAccount?: boolean;
  /** Count of jobs on the job board (for badge) */
  hireJobsCount?: number;
  /** Callback when "Add/Record a Shift" button is clicked */
  onAddShiftClick?: () => void;
  /** Callback when "Job Board" button is clicked */
  onJobBoardClick?: () => void;
  /** Whether to show the "Add Shift" button */
  showAddShiftButton?: boolean;
  /** T2-6334/6365: hide header Add Shift on compact layouts when a footer CTA exists */
  hideAddShiftOnMobile?: boolean;
  /** Whether to show the "Job Board" button */
  showJobBoardButton?: boolean;
  /** Custom action button instead of default buttons */
  customActionButton?: React.ReactNode;
  /** T2-5795: Optional centered page title shown in the navbar. */
  pageTitle?: string;
  /** T2-6717: raise above FullscreenModal so bell/menu stay tappable on Profile */
  elevated?: boolean;
}

const TalentNavbar: React.FC<TalentNavbarProps> = ({
  firstName = '',
  lastName = '',
  photoUrl,
  isHygOrAsst = true,
  instabookActivated = false,
  isTestAccount = false,
  hireJobsCount = 0,
  onAddShiftClick,
  onJobBoardClick,
  showAddShiftButton = true,
  hideAddShiftOnMobile = false,
  showJobBoardButton = true,
  customActionButton,
  pageTitle,
  elevated = false,
}) => {
  const history = useHistory();
  const location = useLocation();
  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);
  const navShellRef = React.useRef<HTMLDivElement | null>(null);
  const currentUserId = (window as any)?.TempStars?.User?.getCurrentUser?.()?.id;
  const ceDirectoryEnabled = useCeDirectoryAvailability(currentUserId) === true;

  const toggleSideMenu = useCallback(() => {
    setIsSideMenuOpen((prev) => {
      const next = !prev;
      if (next && isNativeApp()) {
        applyNativeSafeAreaCssVar();
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!elevated || !navShellRef.current) return;
    const el = navShellRef.current;
    const syncHeight = () => {
      document.documentElement.style.setProperty(
        '--talent-navbar-height',
        `${el.getBoundingClientRect().height}px`,
      );
    };
    syncHeight();
    window.addEventListener('resize', syncHeight);
    return () => {
      window.removeEventListener('resize', syncHeight);
      document.documentElement.style.removeProperty('--talent-navbar-height');
    };
  }, [elevated]);

  const handleAddShift = useCallback(() => {
    if (onAddShiftClick) {
      onAddShiftClick();
    }
  }, [onAddShiftClick]);

  const handleJobBoard = useCallback(() => {
    if (onJobBoardClick) {
      onJobBoardClick();
    } else {
      history.push({ pathname: 'jobPostsList' });
    }
  }, [onJobBoardClick, history]);

  const isNative = isNativeApp();
  const isCompactLayout = useTalentCompactLayout();
  const isMobile = isNative;
  const hideHeaderAddShift = shouldHideTalentHeaderAddShift(hideAddShiftOnMobile, isCompactLayout);
  const showHeaderAddShift = showAddShiftButton && !hideHeaderAddShift;
  const jobAlertsMenuItem = (
    <a
      href="#"
      className={`${styles.menuItem} ${location.pathname.includes('jobAlerts') ? styles.active : ''}`}
      onClick={(e) => {
        e.preventDefault();
        toggleSideMenu();
        history.push({ pathname: 'jobAlerts' });
      }}
      data-testid="talent-job-alerts-menu"
    >
      <NotificationsActiveIcon />
      <span>Job Alerts</span>
    </a>
  );

  return (
    <>
      <div
        ref={navShellRef}
        className={`${styles.navShell} ${elevated ? styles.navShellElevated : ''}`}
      >
        {/* Header */}
        <header className={`${styles.header} ${isMobile ? `${styles.headerMobile} headerMobile` : ''} ${elevated ? styles.headerElevated : ''}`}>
          <div className={styles.headerBar}>
            <div className={styles.headerLeft}>
            <button
              className={styles.logoButton}
              onClick={() => history.push({ pathname: isHygOrAsst ? 'hygienistHome' : 'jobPostsList' })}
              aria-label="Go to home"
            >
              <img src={logoBlue} alt="TempStars" className={styles.logo} />
            </button>
          </div>

          {/* T2-5795: centered page title on web; Cordova renders below the navbar instead. */}
          {pageTitle && !isMobile && (
            <div className={styles.pageTitle} aria-label={pageTitle}>
              {pageTitle}
            </div>
          )}

          <div className={styles.headerActions}>
          {customActionButton ? (
            customActionButton
          ) : (
            <>
              {showHeaderAddShift && (
                <button
                  className={`${styles.addShiftButton} ${isMobile ? styles.addShiftButtonMobile : ''}`}
                  onClick={handleAddShift}
                >
                  <span>+</span>
                  {isMobile ? 'Add Shift' : (
                    <>
                      <span className={styles.addShiftLabelFull}>Add / Record a Shift</span>
                      <span className={styles.addShiftLabelShort}>Add Shift</span>
                    </>
                  )}
                </button>
              )}

              {showJobBoardButton && (
                <button
                  className={`${styles.jobBoardButton} ${isMobile ? styles.jobBoardButtonMobile : ''}`}
                  onClick={handleJobBoard}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
                  </svg>
                  {!isMobile && <span className={styles.jobBoardLabel}>Job Board</span>}
                  {hireJobsCount > 0 && (
                    <span className={styles.jobBoardBadge}>{hireJobsCount}</span>
                  )}
                </button>
              )}
            </>
          )}

          <NotificationBell />

          <div className={styles.headerSeparator}></div>

          <button className={styles.menuButton} onClick={toggleSideMenu} aria-label="Open menu">
            <div className={styles.hamburger}>
              <span className={styles.hamburgerLine}></span>
              <span className={styles.hamburgerLine}></span>
              <span className={styles.hamburgerLine}></span>
            </div>
          </button>
          </div>
        </div>
        </header>
      </div>

      {/* Side Menu */}
      <div className={`${styles.sideMenu} ${isNative ? styles.sideMenuNative : ''} ${isSideMenuOpen ? styles.open : ''}`}>
        <div className={styles.sideMenuHeader}>
          <div className={styles.userProfile}>
            <div
              className={styles.userPhoto}
              style={{ backgroundImage: photoUrl ? `url('${photoUrl}')` : undefined }}
            >
              {!photoUrl && (
                <span className={styles.initials}>
                  {(firstName || 'U').charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div className={styles.userName}>{firstName} {lastName}</div>
          </div>
        </div>
        <nav className={styles.sideMenuNav}>
          <a
            href="#"
            className={`${styles.menuItem} ${location.pathname.includes('hygienistHome') || location.pathname === '/' || (location.pathname === '' && isHygOrAsst) ? styles.active : ''}`}
            onClick={(e) => {
              e.preventDefault();
              toggleSideMenu();
              history.push({ pathname: isHygOrAsst ? 'hygienistHome' : 'jobPostsList' });
            }}
          >
            <HomeIcon />
            <span>Home</span>
          </a>

          {!isHygOrAsst && jobAlertsMenuItem}

          <a
            href="#"
            className={`${styles.menuItem} ${location.pathname.includes('hygienistProfile') ? styles.active : ''}`}
            onClick={(e) => {
              e.preventDefault();
              toggleSideMenu();
              history.push({ pathname: 'hygienistProfile' });
            }}
          >
            <PersonOutlineIcon />
            <span>Profile</span>
          </a>

          {isHygOrAsst && (
            <>
              <a
                href="#"
                className={`${styles.menuItem} ${location.pathname.includes('hygienistJobHistory') ? styles.active : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  toggleSideMenu();
                  history.push({ pathname: 'hygienistJobHistory' });
                }}
              >
                <HistoryIcon />
                <span>Job History</span>
              </a>

              <a
                href="#"
                className={`${styles.menuItem} ${location.pathname.includes('hygienistInvoices') ? styles.active : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  toggleSideMenu();
                  history.push({ pathname: 'hygienistInvoices' });
                }}
              >
                <DescriptionIcon />
                <span>Invoices</span>
              </a>

              {instabookActivated && (
                <a
                  href="#"
                  className={`${styles.menuItem} ${location.pathname.includes('autoBook') ? styles.active : ''}`}
                  onClick={(e) => {
                    e.preventDefault();
                    toggleSideMenu();
                    history.push({ pathname: 'autoBook' });
                  }}
                >
                  <WorkOutlineIcon />
                  <span>Auto-book settings</span>
                </a>
              )}

              <a
                href="#"
                className={`${styles.menuItem} ${location.pathname.includes('jobPostsList') ? styles.active : ''}`}
                data-testid="talent-job-board-menu"
                onClick={(e) => {
                  e.preventDefault();
                  toggleSideMenu();
                  history.push({ pathname: 'jobPostsList' });
                }}
              >
                <WorkOutlineIcon />
                <span>Job Board</span>
              </a>

              {jobAlertsMenuItem}

              <a
                href="#"
                className={`${styles.menuItem} ${location.pathname.includes('myStats') ? styles.active : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  toggleSideMenu();
                  history.push({ pathname: 'myStats' });
                }}
              >
                <BarChartIcon />
                <span>My Stats</span>
              </a>

              <a
                href="#"
                className={`${styles.menuItem} ${location.pathname.includes('coursesHome') ? styles.active : ''}`}
                onClick={(e) => {
                  e.preventDefault();
                  toggleSideMenu();
                  history.push({ pathname: 'coursesHome' });
                }}
              >
                <SchoolIcon />
                <span>{ceDirectoryEnabled ? 'CE Courses' : 'Courses'}</span>
              </a>
            </>
          )}

          <a
            href="#"
            className={`${styles.menuItem} ${location.pathname.includes('hygienistContactSupport') ? styles.active : ''}`}
            onClick={(e) => {
              e.preventDefault();
              toggleSideMenu();
              history.push({ pathname: 'hygienistContactSupport' });
            }}
          >
            <HelpOutlineIcon />
            <span>Support</span>
          </a>

          {isTestAccount && (
            <a
              href="#"
              className={styles.menuItem}
              onClick={(e) => {
                e.preventDefault();
                toggleSideMenu();
                history.push({ pathname: 'MixpanelLogs' });
              }}
            >
              <SearchIcon />
              <span>MixPanel Logs</span>
              <span className={styles.testOnly}>(Test Only)</span>
            </a>
          )}

          <a
            href="#"
            className={`${styles.menuItem} ${styles.logOutItem}`}
            onClick={(e) => {
              e.preventDefault();
              toggleSideMenu();
              try { safeSessionStorage.removeItem('ts_admin_agreed'); safeSessionStorage.removeItem('ts_admin_dismissed'); } catch (_) {}
              if ((window as any).TempStars?.User?.logout) {
                (window as any).TempStars.User.logout().then(() => {
                  window.location.href = '/';
                }).catch(() => {
                  safeLocalStorage.clear();
                  window.location.href = '/';
                });
              } else {
                safeLocalStorage.clear();
                window.location.href = '/';
              }
            }}
          >
            <ExitToAppIcon />
            <span>Log Out</span>
          </a>
        </nav>
      </div>

      {/* Overlay for side menu */}
      {isSideMenuOpen && (
        <div className={styles.overlay} onClick={toggleSideMenu} aria-label="Close menu" />
      )}
    </>
  );
};

export default TalentNavbar;
